import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';

const WebsiteSettingsContext = createContext(null);

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

export const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) return url;
  return `${backendUrl}${url}`;
};

export const WebsiteSettingsProvider = ({ children }) => {
  const query = useQuery({
    queryKey: ['website-settings'],
    queryFn: async () => {
      const res = await api.get('/settings/website');
      return res.data;
    },
    staleTime: 60_000,
  });

  const settings = query.data || null;

  useEffect(() => {
    if (!settings?.faviconUrl) return undefined;
    const href = resolveMediaUrl(settings.faviconUrl);
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = href;
    return undefined;
  }, [settings?.faviconUrl]);

  const socialPlatforms = useMemo(() => {
    if (!settings) return [];
    const list = [
      {
        key: 'facebook',
        label: 'Facebook',
        descriptionEn: 'Follow HKNews for daily headlines and community updates.',
        descriptionKn: 'ದೈನಂದಿನ ಸುದ್ದಿ ಮತ್ತು ಸಮುದಾಯ ನವೀಕರಣಗಳಿಗಾಗಿ HKNews ಅನ್ನು ಅನುಸರಿಸಿ.',
        url: settings.socialFacebook,
      },
      {
        key: 'instagram',
        label: 'Instagram',
        descriptionEn: 'Visual stories, photo features, and behind-the-scenes coverage.',
        descriptionKn: 'ದೃಶ್ಯ ಕಥೆಗಳು, ಫೋಟೋ ವೈಶಿಷ್ಟ್ಯಗಳು ಮತ್ತು ಹಿನ್ನೆಲೆ ವರದಿಗಳು.',
        url: settings.socialInstagram,
      },
      {
        key: 'x',
        label: 'X (Twitter)',
        descriptionEn: 'Breaking news alerts and live updates from Hydrabad Karnataka.',
        descriptionKn: 'ಹೈದರಾಬಾದ್ ಕರ್ನಾಟಕದಿಂದ ತುರ್ತು ಸುದ್ದಿ ಮತ್ತು ಲೈವ್ ನವೀಕರಣಗಳು.',
        url: settings.socialX,
      },
      {
        key: 'youtube',
        label: 'YouTube',
        descriptionEn: 'Video reports, interviews, and e-Paper walkthroughs.',
        descriptionKn: 'ವೀಡಿಯೊ ವರದಿಗಳು, ಸಂದರ್ಶನಗಳು ಮತ್ತು ಇ-ಪೇಪರ್.',
        url: settings.socialYoutube,
      },
      {
        key: 'telegram',
        label: 'Telegram Channel',
        descriptionEn: 'Subscribe for news drops on Telegram.',
        descriptionKn: 'ಟೆಲಿಗ್ರಾಮ್‌ನಲ್ಲಿ ಸುದ್ದಿ ಅಪ್‌ಡೇಟ್‌ಗಳಿಗಾಗಿ ಚಂದಾದಾರರಾಗಿ.',
        url: settings.socialTelegram,
      },
      {
        key: 'whatsapp',
        label: 'WhatsApp Channel',
        descriptionEn: 'Get curated news briefs on WhatsApp.',
        descriptionKn: 'ವಾಟ್ಸಾಪ್‌ನಲ್ಲಿ ಸಂಕಲಿತ ಸುದ್ದಿ ಸಾರಾಂಶಗಳನ್ನು ಪಡೆಯಿರಿ.',
        url: settings.socialWhatsappChannel,
      },
    ];
    return list.filter((p) => Boolean(p.url && String(p.url).trim()));
  }, [settings]);

  const siteName = useCallback(
    (lang) => {
      if (!settings) return lang === 'kn' ? 'ಹೈದರಾಬಾದ್ ಕರ್ನಾಟಕ' : 'Hydrabad Karnataka News';
      return lang === 'kn'
        ? (settings.siteNameKn || settings.siteNameEn)
        : (settings.siteNameEn || settings.siteNameKn);
    },
    [settings]
  );

  const tagline = useCallback(
    (lang) => {
      if (!settings) return '';
      return lang === 'kn'
        ? (settings.taglineKn || settings.taglineEn)
        : (settings.taglineEn || settings.taglineKn);
    },
    [settings]
  );

  const value = useMemo(
    () => ({
      settings,
      loading: query.isLoading,
      error: query.error,
      refetch: query.refetch,
      socialPlatforms,
      siteName,
      tagline,
      resolveMediaUrl,
    }),
    [settings, query.isLoading, query.error, query.refetch, socialPlatforms, siteName, tagline]
  );

  return (
    <WebsiteSettingsContext.Provider value={value}>{children}</WebsiteSettingsContext.Provider>
  );
};

export const useWebsiteSettingsOptional = () => useContext(WebsiteSettingsContext);

export const useWebsiteSettings = () => {
  const ctx = useContext(WebsiteSettingsContext);
  if (!ctx) throw new Error('useWebsiteSettings must be used within WebsiteSettingsProvider');
  return ctx;
};
